import { ReactElement, useMemo } from "react";
import { FaGoogle, FaDiscord } from "react-icons/fa";
import { isGoNative } from "@/utils/isGoNative";

export enum SocialProvider {
    GOOGLE = "google",
    DISCORD = "discord",
    X = "x",
    TWITCH = "twitch",
    STEAM = "steam",
    EMAIL = "email",
    PHONE = "phone",
}

export interface ISocialProvider {
    id: SocialProvider;
    name: string;
    icon: ReactElement;
    href: string;
}

interface SocialLoginButtonsProps {
    onProviderSelect: (provider: ISocialProvider) => void;
    isLoading?: boolean;
    selectedProvider?: SocialProvider | null;
}

export const SOCIAL_PROVIDERS: Array<ISocialProvider> = [
    {
        id: SocialProvider.DISCORD,
        name: "Discord",
        icon: <FaDiscord />,
        href: `${import.meta.env.VITE_BACKEND_REST_URL}/passport/discord/login`,
    },
    /*
     * Temporarily disabled - these providers don't provide email
     * {
     *  id: SocialProvider.STEAM,
     *  name: "Steam",
     *  icon: <SiSteam />
     * },
     * {
     *  id: SocialProvider.TWITCH,
     *  name: "Twitch",
     *  icon: <SiTwitch />
     * },
     */
    {
        id: SocialProvider.GOOGLE,
        name: "Google",
        icon: <FaGoogle />,
        href: `${import.meta.env.VITE_BACKEND_REST_URL}/passport/google/login`,
    },
    /*
     * Temporarily disabled - doesn't provide email
     * {
     *  id: SocialProvider.X,
     *  name: "X",
     *  icon: <FaXTwitter />
     * }
     */
];

export const SocialLoginStandaloneButton = ({provider, onClick}: { provider: ISocialProvider, onClick?: (provider: ISocialProvider) => void }) => {
    return <button
        key={provider.id}
        className={"social-login-buttons__icon-button"}
        onClick={() => onClick?.(provider)}
        aria-label={`Login with ${provider.name}`}
        style={{ cursor: onClick ? "pointer" : "not-allowed" }}
    >
        {provider.icon}
    </button>;
};

function SocialLoginButtons({
    onProviderSelect,
}: SocialLoginButtonsProps): ReactElement {

    // Filter out Google login when in GoNative/Median app (Google blocks OAuth in WebViews)
    const availableProviders = useMemo(() => {
        if (isGoNative()) {
            return SOCIAL_PROVIDERS.filter(p => p.id !== SocialProvider.GOOGLE);
        }
        return SOCIAL_PROVIDERS;
    }, []);

    return (
        <div className="social-login-buttons">
            <div className="social-login-buttons__icon-row">
                {availableProviders.map((provider) =>
                    <SocialLoginStandaloneButton key={provider.id} provider={provider} onClick={onProviderSelect}/>)}
            </div>

        </div>
    );
}

export default SocialLoginButtons;